import { useState } from 'react'
import { isAxiosError } from 'axios'
import { updateCustomer, verifyCustomer } from '@/api/customers'
import { useAuthStore } from '@/store/authStore'
import { hasPermission } from '@/lib/permissions'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SubscriptionStatusBadge } from '@/components/SubscriptionStatusBadge'
import { CustomerRecordingPlayer } from '@/components/customer/CustomerRecordingPlayer'
import { CustomerValidationBanner } from '@/components/customer/CustomerValidationBanner'
import { formatCustomerNumber, getCustomerStartDate } from '@/lib/customer'
import { isCustomerVerified } from '@/lib/validation'
import type { Customer } from '@/api/types'
import type { UpdateCustomerPayload } from '@/api/customers'

type NullableCustomerFields = Omit<UpdateCustomerPayload, 'identity_number'>

type CustomerFormState = Record<keyof UpdateCustomerPayload, string>

const IDENTITY_NUMBER_LABEL = 'Personnummer'

const DUPLICATE_IDENTITY_NUMBER_STATUS = 409

const APPROVE_LABEL = 'Godkänn'

const APPROVING_LABEL = 'Godkänner...'

const APPROVE_CONFIRM_MESSAGE = 'Vill du godkänna kundens uppgifter? Leveranser bokas automatiskt, och kunden slutar synkas från Hitrate.'

const APPROVE_ERROR_MESSAGE = 'Kunde inte godkänna kunden just nu.'

const EDITABLE_FIELDS: { key: keyof NullableCustomerFields; label: string; wide?: boolean }[] = [
  { key: 'first_name', label: 'Förnamn' },
  { key: 'last_name', label: 'Efternamn' },
  { key: 'email', label: 'E-post' },
  { key: 'phone', label: 'Telefon' },
  { key: 'address', label: 'Adress', wide: true },
  { key: 'postal_code', label: 'Postnummer' },
  { key: 'city', label: 'Ort' }
]

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('sv-SE')
}

function getCustomerName(customer: Customer): string {
  const name = `${customer.first_name ?? ''} ${customer.last_name ?? ''}`.trim()

  if (name.length === 0) {
    return 'Okänd kund'
  }

  return name
}

function buildFormState(customer: Customer): CustomerFormState {
  return {
    identity_number: customer.identity_number ?? '',
    first_name: customer.first_name ?? '',
    last_name: customer.last_name ?? '',
    email: customer.email ?? '',
    phone: customer.phone ?? '',
    address: customer.address ?? '',
    postal_code: customer.postal_code ?? '',
    city: customer.city ?? ''
  }
}

function buildPayload(form: CustomerFormState, identityNumber: string): UpdateCustomerPayload {
  const nullableFields = {} as NullableCustomerFields

  for (const field of EDITABLE_FIELDS) {
    const value = form[field.key].trim()

    nullableFields[field.key] = value.length > 0 ? value : null
  }

  return { ...nullableFields, identity_number: identityNumber }
}

function getSaveErrorMessage(error: unknown): string {
  const isDuplicateIdentityNumber = isAxiosError(error) && error.response?.status === DUPLICATE_IDENTITY_NUMBER_STATUS

  if (isDuplicateIdentityNumber) {
    return 'Personnumret används redan av en annan kund.'
  }

  return 'Kunde inte spara kunduppgifterna just nu.'
}

interface CustomerFieldInputProps {
  fieldKey: keyof UpdateCustomerPayload
  label: string
  value: string
  isWide?: boolean
  onChange: (key: keyof UpdateCustomerPayload, value: string) => void
}

function CustomerFieldInput({ fieldKey, label, value, isWide, onChange }: CustomerFieldInputProps): React.ReactElement {
  return (
    <div className={`space-y-2 ${isWide ? 'sm:col-span-2' : ''}`}>
      <Label
        htmlFor={`customer-${fieldKey}`}
        className="text-sm text-muted-foreground"
      >
        {label}
      </Label>
      <Input
        id={`customer-${fieldKey}`}
        value={value}
        onChange={(event) => onChange(fieldKey, event.target.value)}
        className="rounded-none"
      />
    </div>
  )
}

interface CustomerInfoCardProps {
  customer: Customer
  onCustomerChanged: (customer: Customer) => void
}

export function CustomerInfoCard({ customer, onCustomerChanged }: CustomerInfoCardProps): React.ReactElement {
  const user = useAuthStore((state) => state.user)
  const canEdit = hasPermission(user, 'customers.write')
  const [isEditing, setIsEditing] = useState(false)
  const [form, setForm] = useState<CustomerFormState>(buildFormState(customer))
  const [isSaving, setIsSaving] = useState(false)
  const [isApproving, setIsApproving] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const hasRecording = customer.order_recording_id !== null
  const isVerified = isCustomerVerified(customer)
  const approveLabel = isApproving ? APPROVING_LABEL : APPROVE_LABEL
  const syncStatus = customer.manually_updated_at
    ? `Manuellt ändrad ${formatDate(customer.manually_updated_at)} – synkas inte längre från Hitrate`
    : 'Synkas från Hitrate'

  const handleStartEditing = (): void => {
    setForm(buildFormState(customer))
    setErrorMessage(null)
    setIsEditing(true)
  }

  const handleCancel = (): void => {
    setErrorMessage(null)
    setIsEditing(false)
  }

  const handleApprove = async (): Promise<void> => {
    const isConfirmed = window.confirm(APPROVE_CONFIRM_MESSAGE)

    if (!isConfirmed) {
      return
    }

    setIsApproving(true)
    setErrorMessage(null)

    try {
      const updated = await verifyCustomer(customer.id)

      onCustomerChanged(updated)
    } catch {
      setErrorMessage(APPROVE_ERROR_MESSAGE)
    } finally {
      setIsApproving(false)
    }
  }

  const handleChange = (key: keyof UpdateCustomerPayload, value: string): void => {
    setForm((current) => ({ ...current, [key]: value }))
  }

  const handleSave = async (): Promise<void> => {
    const identityNumber = form.identity_number.trim()

    if (identityNumber.length === 0) {
      setErrorMessage(`${IDENTITY_NUMBER_LABEL} måste fyllas i.`)
      return
    }

    setIsSaving(true)
    setErrorMessage(null)

    const payload = buildPayload(form, identityNumber)

    try {
      const updated = await updateCustomer(customer.id, payload)

      onCustomerChanged(updated)
      setIsEditing(false)
    } catch (error) {
      setErrorMessage(getSaveErrorMessage(error))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Card className="shadow-none border-border/80">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>{getCustomerName(customer)}</CardTitle>
          <CardDescription>
            {formatCustomerNumber(customer.id)} · Kunduppgifter · {syncStatus}
          </CardDescription>
        </div>
        {!isEditing && canEdit && (
          <div className="flex shrink-0 gap-2">
            <Button
              variant="outline"
              className="rounded-none"
              onClick={handleStartEditing}
            >
              Ändra
            </Button>
            {!isVerified && (
              <Button
                className="rounded-none"
                disabled={isApproving}
                onClick={() => void handleApprove()}
              >
                {approveLabel}
              </Button>
            )}
          </div>
        )}
        {isEditing && (
          <div className="flex shrink-0 gap-2">
            <Button
              variant="outline"
              className="rounded-none"
              disabled={isSaving}
              onClick={handleCancel}
            >
              Avbryt
            </Button>
            <Button
              className="rounded-none"
              disabled={isSaving}
              onClick={handleSave}
            >
              Spara
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <CustomerValidationBanner customer={customer} />

        {isEditing && (
          <CustomerFieldInput
            fieldKey="identity_number"
            label={IDENTITY_NUMBER_LABEL}
            value={form.identity_number}
            onChange={handleChange}
          />
        )}

        {isEditing &&
          EDITABLE_FIELDS.map((field) => (
            <CustomerFieldInput
              key={field.key}
              fieldKey={field.key}
              label={field.label}
              value={form[field.key]}
              isWide={field.wide}
              onChange={handleChange}
            />
          ))}

        {!isEditing && (
          <>
            <div>
              <p className="text-sm text-muted-foreground">E-post</p>
              <p>{customer.email ?? '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Telefon</p>
              <p>{customer.phone ?? '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{IDENTITY_NUMBER_LABEL}</p>
              <p>{customer.identity_number ?? '-'}</p>
            </div>
          </>
        )}

        <div>
          <p className="text-sm text-muted-foreground">Säljare</p>
          <p>{customer.hitrate_agent_name || 'N/A'}</p>
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Abonnemang</p>
          <SubscriptionStatusBadge subscription={customer.subscription} />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">Skapad</p>
          <p>{formatDate(getCustomerStartDate(customer))}</p>
        </div>

        {!isEditing && (
          <>
            <div className="sm:col-span-2">
              <p className="text-sm text-muted-foreground">Adress</p>
              <p>{customer.address ?? '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Postnummer</p>
              <p>{customer.postal_code ?? '-'}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Ort</p>
              <p>{customer.city ?? '-'}</p>
            </div>
          </>
        )}

        {hasRecording && (
          <div className="sm:col-span-2">
            <p className="text-sm text-muted-foreground">Samtalsinspelning</p>
            <div className="mt-2">
              <CustomerRecordingPlayer customerId={customer.id} />
            </div>
          </div>
        )}

        {errorMessage && <p className="text-sm text-destructive sm:col-span-2">{errorMessage}</p>}
      </CardContent>
    </Card>
  )
}
